import { fetchUtils } from 'react-admin';
import { persistStorageService } from '@/features/auth/persist-storage';
import { OrgLocation } from '@/features/organizations/organizations.types';

interface AgentHookProps {
  apiUrl: string;
  fetchJson: (
    url: string,
    options?: fetchUtils.Options,
  ) => Promise<{ json: any }>;
}

// TODOL This is not a react hook, need to move to correct path
export const agentService = ({ apiUrl, fetchJson }: AgentHookProps) => {
  const handleAgentCreate = async (params: any) => {
    const selectedOrg = persistStorageService.getPersistItem<OrgLocation>({
      name: 'selectedOrganisation',
      parse: true,
    });

    const selectedLoc = persistStorageService.getPersistItem<OrgLocation>({
      name: 'selectedLocation',
      parse: true,
    });
    // Extract the related records
    const { chatbots, tools, ...data } = params.data;

    // Create the agent first
    const response = await fetchJson(`${apiUrl}/${params.resource}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    const agent = await response.json;

    // If we have chatbots, create the associations
    if (chatbots && chatbots.length > 0) {
      const chatbotPromises = chatbots.map((chatbotId: string) =>
        fetchJson(`${apiUrl}/api/v1/chatbots_agents`, {
          method: 'POST',
          body: JSON.stringify({
            agents_id: agent.id,
            chatbots_id: chatbotId,
            is_active: true,
            deleted: false,
          }),
        }),
      );
      await Promise.all(chatbotPromises);
    }

    // If we have tools, create the associations
    if (tools && tools.length > 0) {
      const toolPromises = tools.map((toolId: string) =>
        fetchJson(`${apiUrl}/api/v1/tools_agents`, {
          method: 'POST',
          body: JSON.stringify({
            agents_id: agent.id,
            tools_id: toolId,
            deleted: false,
            organisation_id: selectedOrg?.id || '',
            location_id: selectedLoc?.id || '',
          }),
        }),
      );
      await Promise.all(toolPromises);
    }

    return { data: agent };
  };

  const handleAgentUpdate = async (params: any) => {
    const selectedOrg = persistStorageService.getPersistItem<OrgLocation>({
      name: 'selectedOrganisation',
      parse: true,
    });

    const selectedLoc = persistStorageService.getPersistItem<OrgLocation>({
      name: 'selectedLocation',
      parse: true,
    });
    // Extract the related records
    const { chatbots, tools, ...data } = params.data;

    // Update the agent first
    const response = await fetchJson(
      `${apiUrl}/${params.resource}/${params.id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
    );

    const agent = await response.json;

    // If we have chatbots, update the associations
    if (chatbots) {
      // First, get existing associations
      const existingResponse = await fetchJson(
        `${apiUrl}/chatbots_agents?agents_id=${params.id}`,
        { method: 'GET' },
      );
      const existingAssociations = await existingResponse.json;

      // Delete existing associations by marking them as deleted
      await Promise.all(
        existingAssociations.map((assoc: any) =>
          fetchJson(`${apiUrl}/api/v1/chatbots_agents/${assoc.id}`, {
            method: 'PATCH',
            body: JSON.stringify({ deleted: true }),
          }),
        ),
      );

      // Create new associations
      if (chatbots.length > 0) {
        const chatbotPromises = chatbots.map((chatbotId: string) =>
          fetchJson(`${apiUrl}/api/v1/chatbots_agents`, {
            method: 'POST',
            body: JSON.stringify({
              agents_id: agent.id,
              chatbots_id: chatbotId,
              is_active: true,
              deleted: false,
            }),
          }),
        );
        await Promise.all(chatbotPromises);
      }
    }

    // Similar approach for tools
    if (tools) {
      // First, get existing associations
      const existingResponse = await fetchJson(
        `${apiUrl}/api/v1/tools_agents?agents_id=${params.id}`,
        { method: 'GET' },
      );
      const existingAssociations = await existingResponse.json;

      // Delete existing associations
      await Promise.all(
        existingAssociations.map((assoc: any) =>
          fetchJson(`${apiUrl}/api/v1/tools_agents/${assoc.id}`, {
            method: 'PATCH',
            body: JSON.stringify({ deleted: true }),
          }),
        ),
      );

      // Create new associations
      if (tools.length > 0) {
        const toolPromises = tools.map((toolId: string) =>
          fetchJson(`${apiUrl}/api/v1/tools_agents`, {
            method: 'POST',
            body: JSON.stringify({
              agents_id: agent.id,
              tools_id: toolId,
              deleted: false,
              organisation_id: selectedOrg?.id || '',
              location_id: selectedLoc?.id || '',
            }),
          }),
        );
        await Promise.all(toolPromises);
      }
    }

    return { data: agent };
  };

  // Modify the getOne method to include related entities
  const handleAgentGetOne = async (params: any) => {
    const response = await fetchJson(
      `${apiUrl}/${params.resource}/${params.id}`,
    );
    const agent = await response.json;

    // Get associated chatbots
    const chatbotsResponse = await fetchJson(
      `${apiUrl}/api/v1/chatbots_agents?agents_id=${params.id}&deleted=false`,
      { method: 'GET' },
    );
    const chatbotsAssociations = await chatbotsResponse.json;

    // Get associated tools
    const toolsResponse = await fetchJson(
      `${apiUrl}/api/v1/tools_agents?agents_id=${params.id}&deleted=false`,
      { method: 'GET' },
    );
    const toolsAssociations = await toolsResponse.json;

    // Add these to the agent object
    return {
      data: {
        ...agent,
        chatbots: chatbotsAssociations.map((ca: any) => ca.chatbots_id),
        tools: toolsAssociations.map((ta: any) => ta.tools_id),
      },
    };
  };

  return {
    handleAgentCreate,
    handleAgentUpdate,
    handleAgentGetOne,
  };
};
